import { ErrorService } from '@/services/error-service.js';
import usergroupsAPI from '@/api/usergroupsAPI';
import { slugify } from '@/utils.js';

const DEV_AUTH = process.env.NODE_ENV === 'development' ? true : false;
const AUTH = {
  username: process.env.VUE_APP_LOGIN_API_USERNAME,
  password: process.env.VUE_APP_LOGIN_API_PASSWORD
};
const path = require('path');
const DOMAIN = process.env.VUE_APP_DOMAIN;
const USERGROUP_API_PATH = process.env.VUE_APP_USERGROUP_API_PATH;

/**************** STATE *******************/
const state = {
  usergroupsTypes: [],
  usergroupsList: [],
  organisationsList: [],
  groupsList: [],
  spheresList: [],
  usergroupsRoles: [],
  usergroupsError: null,
  searchQuery: null
};

/**************** GETTERS *****************/
const getters = {};

/*************** MUTATIONS ****************/
const mutations = {

  SET_USERGROUPS_TYPES: (state, payload) => {
    state.usergroupsTypes = payload;
  },

  SET_USERGROUPS_ROLES: (state, payload) => {
    state.usergroupsRoles = payload;
  },

  SET_USERGROUPS_LIST: (state, payload) => {
    if (payload && payload.results && payload.results.length) {
      state.usergroupsList = payload.results;
    }
  },
  SET_ORGANISATIONS_LIST: (state, payload) => {
    if (payload && payload.results && payload.results.length) {
      state.organisationsList = payload.results;
    }
  },
  SET_GROUPS_LIST: (state, payload) => {
    if (payload && payload.results && payload.results.length) {
      state.groupsList = payload.results;
    }
  },
  SET_SPHERES_LIST: (state, payload) => {
    if (payload && payload.results && payload.results.length) {
      state.spheresList = payload.results;
    }
  },

  SET_ERROR: (state, error) => {
    if (error) {
      ErrorService.onError(error);
      state.usergroupsError = error;
    } else {
      state.usergroupsError = error;
    }
  },

  SET_USERGROUPS_SEARCH_QUERY: (state, payload) => {
    state.searchQuery = payload;
  }

};
/**************** ACTIONS *****************/
const actions = {

  GET_USERGROUPS_TYPES: async ({ commit }) => {
    const usergroupsTypes = await usergroupsAPI.getUsergroupsTypes();
    commit('SET_USERGROUPS_TYPES', usergroupsTypes);
  },

  GET_USERGROUPS_LIST: async ({ commit }, usergroupsTypes) => {
    const usergroups = await usergroupsAPI.getUsergroupsList(usergroupsTypes);
    if (usergroupsTypes.length === 0) {
      commit('SET_USERGROUPS_LIST', usergroups);
    }
    if (usergroupsTypes.length === 1) {
      switch (usergroupsTypes[0]) {
        case 'organisation':
          commit('SET_ORGANISATIONS_LIST', usergroups);
          break;
        case 'user-group':
          commit('SET_GROUPS_LIST', usergroups);
          break;
        case 'group-of-organisation':
          commit('SET_SPHERES_LIST', usergroups);
          break;
      }
    }
  },

  SEARCH_USERGROUPS: async ({ state, rootState, commit }, usergroupsTypes) => {
    if (rootState.abortControllers.length > 0) {
      commit('USE_ABORT_CONTROLLER', 'search_usergroups', { root: true });
    }
    const controller = new AbortController();
    commit('SET_ABORT_CONTROLLER', {
      id: 'search_usergroups',
      controller: controller
    }, { root: true });


    try {
      const usergroups = await usergroupsAPI.getUsergroupsList(
        usergroupsTypes,
        state.searchQuery,
        controller
      );
      commit('REMOVE_ABORT_CONTROLLER', 'search_usergroups', { root: true });
      if (usergroups) {
        commit('SET_ERROR', null);
        if (usergroupsTypes.length === 0) {
          commit('SET_USERGROUPS_LIST', usergroups);
        }
        if (usergroupsTypes.length === 1) {
          switch (usergroupsTypes[0]) {
            case 'organisation':
              commit('SET_ORGANISATIONS_LIST', usergroups);
              break;
            case 'user-group':
              commit('SET_GROUPS_LIST', usergroups);
              break;
            case 'group-of-organisation':
              commit('SET_SPHERES_LIST', usergroups);
              break;
          }
        }
      }
    } catch(err) {
      commit('REMOVE_ABORT_CONTROLLER', 'search_usergroups', { root: true });
    }
  },

  CREATE_USERGROUP: async ({ rootState }, { form, thumbnail, spheres }) => {
    const data = {
      organisation: {
        additional_id: form.additional_id,
        display_name: form.display_name,
        codename: slugify(form.display_name),
        acronym: form.acronym,
        registration_number: form.registration_number,
        organisation_type: rootState.organisations.organisationsTypes.find(el => {
          return el.display_name === form.organisation_type.label
        }),
        is_active: true,
        postal_address: form.postal_address,
        description: form.description,
        website_url: form.website_url,
        phone_number: form.phone_number
      },
      display_name: form.display_name,
      codename: slugify(form.display_name),
      usergroup_type: 'organisation',
    };
    const createdUsergroup = await usergroupsAPI.createUsergroup(data);
    if (createdUsergroup && createdUsergroup.id) {
      if (thumbnail) {
        await usergroupsAPI.setUsergroupThumbnail(
          createdUsergroup.id,
          thumbnail
        );
      }
      if (spheres && spheres.length > 0) {
        const parentsIds = spheres.map((el) => { return el.id; });
        for (const parentId of parentsIds) {
          await usergroupsAPI.addParentToUsergroup(createdUsergroup.id, parentId);
        }
      }
    }
    return createdUsergroup;
  },

  GET_USERGROUPS_ROLES: async ({ commit }) => {
    const roles = await usergroupsAPI.getUsergroupsRoles();
    commit('SET_USERGROUPS_ROLES', roles);
  },

  GET_USERGROUP_MEMBERS: async ({}, id) => {
    try {
      const members = await usergroupsAPI.getUsergroupMembers(id);
      if (members && members.results) {
        return members.results;
      }
    } catch (err) {
      throw err;
    }
  },

  ADD_USER_TO_USERGROUP: async ({}, { usergroup, data }) => {
    try {
      const response = await usergroupsAPI.addUserToUsergroup(usergroup.id, data);
      if (response) {
        ErrorService.onSuccess(response, `Vous avez bien été ajouté à l\'organisation ${usergroup.display_name}`);
      }
    } catch (err) {
      throw err;
    }
  },
  REMOVE_USER_FROM_USERGROUP: async ({}, { usergroup, userId}) => {
    try {
      const response = await usergroupsAPI.removeUserFromUsergroup(usergroup.id, userId);
      if (response) {
        ErrorService.onSuccess(response, `Vous avez bien été retiré de l\'organisation ${usergroup.display_name}`);
      }
    } catch (err) {
      throw err;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
